<div
  class="fi-trigger"
  tabindex="0"
  role="button"
  matTooltip="Open the FOTA flow diagrams and glossary"
  aria-label="How FOTA Works"
  (click)="open()"
  (keydown.enter)="open()"
>
  <mat-icon class="fi-trigger-icon">menu_book</mat-icon>
</div>

<div
  class="custom-container fota-info-root info-panel-root"
  [class.active]="isOpen"
>
  <div class="custom-modal custom-modal-l">
    <div class="custom-modal-dialog">
      <div class="custom-modal-content">
        <div class="custom-modal-header">
          <h4 class="custom-modal-title">How FOTA Works</h4>
          <div class="fi-header-actions">
            <button
              type="button"
              class="fi-export-btn"
              (click)="exportPdf()"
              aria-label="Export as PDF"
            >
              <mat-icon>picture_as_pdf</mat-icon>
              Export as PDF
            </button>
            <button type="button" class="custom-close-btn" (click)="close()">
              &times;
            </button>
          </div>
        </div>

        <div class="custom-modal-body fi-body">
          <p class="fi-intro">
            FOTA pushes new firmware to a device over its regular check-in
            connection &mdash; no batch, manual request, or ticket ever talks
            to the device directly. This traces what actually happens between
            a batch/device being assigned and a device showing
            <span class="fi-pill fi-pill-ok">Completed</span>, plus the rule
            the system uses when a device is asked to do two things at once.
          </p>

          <section class="fi-section">
            <h5 class="fi-h5">1. Which request wins</h5>
            <p class="fi-p">
              A device can only run <strong>one</strong> FOTA assignment at a
              time. Regular batches, AIS140 batches, manual AIS140 flags, and
              auto-generated AIS140 batches all compete for that one slot
              &mdash; a fixed priority decides who gets it.
            </p>

            <figure class="fi-figure">
              <svg
                viewBox="0 0 720 400"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Two scenarios showing how a higher-priority FOTA request supersedes a running lower-priority one, while a lower-priority request is rejected outright when a higher one is already running."
              >
                <defs>
                  <marker
                    id="fiArrow"
                    viewBox="0 0 10 10"
                    refX="8"
                    refY="5"
                    markerWidth="7"
                    markerHeight="7"
                    orient="auto-start-reverse"
                  >
                    <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
                  </marker>
                  <marker
                    id="fiArrowMuted"
                    viewBox="0 0 10 10"
                    refX="8"
                    refY="5"
                    markerWidth="7"
                    markerHeight="7"
                    orient="auto-start-reverse"
                  >
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--theme-text-muted)" />
                  </marker>
                </defs>

                <text x="20" y="26" font-size="9.4" font-weight="600" fill="var(--theme-text-muted)" letter-spacing="0.03em">CASE 1 &mdash; A HIGHER-PRIORITY REQUEST ARRIVES</text>

                <rect x="20" y="46" width="200" height="58" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="120" y="70" text-anchor="middle" font-size="9.8" font-weight="600" fill="var(--theme-text)">Device running</text>
                <text x="120" y="88" text-anchor="middle" font-size="9.4" fill="var(--theme-text-muted)">Regular Batch #1180</text>

                <line x1="228" y1="75" x2="288" y2="75" stroke="currentColor" stroke-width="1.6" marker-end="url(#fiArrow)" />
                <text x="258" y="65" text-anchor="middle" font-size="8.2" fill="var(--info-accent-strong)" font-weight="600">new AIS140 request</text>

                <rect x="296" y="40" width="210" height="58" rx="9" fill="var(--info-accent-fill)" stroke="var(--info-accent)" stroke-width="1.6" />
                <text x="401" y="64" text-anchor="middle" font-size="9.8" font-weight="700" fill="var(--info-accent-strong)">Device now running</text>
                <text x="401" y="82" text-anchor="middle" font-size="9.4" fill="var(--info-accent-strong)">AIS140 Batch #1214</text>

                <line x1="401" y1="98" x2="401" y2="118" stroke="var(--theme-text-muted)" stroke-width="1.4" stroke-dasharray="3 3" marker-end="url(#fiArrowMuted)" />
                <rect x="296" y="120" width="210" height="36" rx="8" fill="var(--info-danger-fill)" stroke="var(--info-danger)" stroke-dasharray="4 3" />
                <text x="401" y="143" text-anchor="middle" font-size="9.0" fill="var(--info-danger)" font-weight="600">Regular #1180 &rarr; Aborted (superseded)</text>

                <text x="20" y="212" font-size="9.4" font-weight="600" fill="var(--theme-text-muted)" letter-spacing="0.03em">CASE 2 &mdash; A LOWER-PRIORITY REQUEST ARRIVES</text>

                <rect x="20" y="232" width="210" height="58" rx="9" fill="var(--info-accent-fill)" stroke="var(--info-accent)" stroke-width="1.6" />
                <text x="125" y="256" text-anchor="middle" font-size="9.8" font-weight="700" fill="var(--info-accent-strong)">Device running</text>
                <text x="125" y="274" text-anchor="middle" font-size="9.4" fill="var(--info-accent-strong)">AIS140 Batch #1214</text>

                <line x1="238" y1="261" x2="298" y2="261" stroke="currentColor" stroke-width="1.6" marker-end="url(#fiArrow)" />
                <text x="268" y="251" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)" font-weight="600">new Regular request</text>

                <rect x="306" y="232" width="210" height="58" rx="9" fill="var(--info-danger-fill)" stroke="var(--info-danger)" stroke-dasharray="4 3" />
                <text x="411" y="256" text-anchor="middle" font-size="9.8" font-weight="700" fill="var(--info-danger)">Rejected outright</text>
                <text x="411" y="274" text-anchor="middle" font-size="9.0" fill="var(--info-danger)">device keeps running #1214</text>

                <text x="560" y="200" text-anchor="middle" font-size="8.6" font-weight="700" fill="var(--theme-text)" transform="rotate(-90 560 200)">HIGHER PRIORITY</text>
                <line x1="600" y1="330" x2="600" y2="60" stroke="var(--theme-text-muted)" stroke-width="1.4" marker-end="url(#fiArrowMuted)" />
                <rect x="614" y="46" width="96" height="30" rx="6" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="662" y="65" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">Auto AIS140</text>
                <rect x="614" y="150" width="96" height="30" rx="6" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="662" y="169" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">AIS140</text>
                <rect x="614" y="254" width="96" height="30" rx="6" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="662" y="273" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">Regular</text>
              </svg>
              <figcaption class="fi-caption">
                A device holds exactly one live FOTA assignment.
                <strong>Case 1:</strong> an AIS140-flagged request (batch,
                manual, or auto-generated) always takes the slot, silently
                aborting whatever Regular assignment was running.
                <strong>Case 2:</strong> the reverse never happens &mdash; a
                Regular request submitted against a device already on an
                AIS140 assignment is rejected before it's even written, and
                the AIS140 run continues untouched.
              </figcaption>
            </figure>

            <ul class="fi-rule-list">
              <li>
                <strong>1. Auto AIS140 Batch</strong> (created automatically
                from an AIS140 certification ticket) &mdash; always wins.
                Blocks every other new request on that device; nothing but
                another auto batch can replace it.
              </li>
              <li>
                <strong>2. AIS140 Batch / Manual FOTA flagged AIS140</strong>
                &mdash; wins over anything Regular; loses only to an Auto
                AIS140 Batch.
              </li>
              <li>
                <strong>3. Regular Batch / Manual FOTA (not AIS140)</strong>
                &mdash; lowest priority. Takes the slot only if it's free; is
                silently replaced the moment any AIS140-tier request shows up.
              </li>
            </ul>
            <p class="fi-p">
              The logic: AIS140 certification is a government compliance
              requirement, so a compliance-driven firmware push &mdash;
              whether a person ticked the AIS140 box or a certification
              ticket triggered it automatically &mdash; must never be
              silently overtaken by a routine bulk rollout. Manual FOTA gets
              no priority of its own; it's classified
              <code>AIS140</code> or <code>Regular</code> by its own checkbox
              and follows the exact same rule.
            </p>
          </section>

          <section class="fi-section">
            <h5 class="fi-h5">2. What happens to an assigned device</h5>
            <p class="fi-p">
              Once a device holds the FOTA slot, nothing happens until that
              device sends its next login packet &mdash; the server never
              opens a connection to a device. Everything below runs inside
              that check-in exchange, repeated login after login until the
              device is fully done or aborted.
            </p>

            <figure class="fi-figure">
              <svg
                viewBox="0 0 760 1050"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="Flowchart of a device's FOTA execution: eligibility checks, firmware version comparison, chunked download with a three-attempt retry limit, then sequential IP and state provisioning, with abort and pause branches."
              >
                <defs>
                  <marker id="fiArrow2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
                  </marker>
                  <marker id="fiArrowOk" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-ok)" />
                  </marker>
                  <marker id="fiArrowDanger" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-danger)" />
                  </marker>
                  <marker id="fiArrowMuted2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--theme-text-muted)" />
                  </marker>
                </defs>

                <rect x="170" y="10" width="260" height="46" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="300" y="38" text-anchor="middle" font-size="9.8" font-weight="600" fill="var(--theme-text)">Device sends a login packet</text>
                <line x1="300" y1="56" x2="300" y2="78" stroke="currentColor" stroke-width="1.5" marker-end="url(#fiArrow2)" />

                <polygon points="300,80 430,130 300,180 170,130" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="300" y="124" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">Unrelated OTA</text>
                <text x="300" y="137" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">command pending?</text>
                <line x1="430" y1="130" x2="478" y2="130" stroke="currentColor" stroke-width="1.5" marker-end="url(#fiArrow2)" />
                <text x="454" y="121" text-anchor="middle" font-size="7.8" fill="var(--theme-text-muted)">yes</text>
                <rect x="480" y="105" width="220" height="50" rx="8" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="590" y="125" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Run that command instead</text>
                <text x="590" y="140" text-anchor="middle" font-size="7.8" fill="var(--theme-text-muted)">FOTA is skipped this cycle</text>

                <line x1="300" y1="180" x2="300" y2="203" stroke="currentColor" stroke-width="1.5" marker-end="url(#fiArrow2)" />
                <text x="316" y="196" font-size="7.8" fill="var(--theme-text-muted)">no</text>

                <polygon points="300,205 430,255 300,305 170,255" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="300" y="243" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">Ignition ON &amp;</text>
                <text x="300" y="256" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">FOTA slot free?</text>
                <text x="300" y="269" text-anchor="middle" font-size="7.0" fill="var(--theme-text-muted)">(AIS140 always free)</text>
                <line x1="430" y1="255" x2="478" y2="255" stroke="currentColor" stroke-width="1.5" marker-end="url(#fiArrow2)" />
                <text x="454" y="246" text-anchor="middle" font-size="7.8" fill="var(--theme-text-muted)">no</text>
                <rect x="480" y="230" width="220" height="50" rx="8" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="590" y="250" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Wait</text>
                <text x="590" y="265" text-anchor="middle" font-size="7.8" fill="var(--theme-text-muted)">try again on the next login</text>

                <line x1="300" y1="305" x2="300" y2="328" stroke="currentColor" stroke-width="1.5" marker-end="url(#fiArrow2)" />
                <text x="316" y="321" font-size="7.8" fill="var(--theme-text-muted)">yes</text>

                <polygon points="300,330 430,380 300,430 170,380" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="300" y="374" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">Reported firmware</text>
                <text x="300" y="387" text-anchor="middle" font-size="8.2" fill="var(--theme-text)">= target version?</text>
                <text x="300" y="399" text-anchor="middle" font-size="6.6" fill="var(--info-accent)">(Mandatory forces "no" &lt;100%)</text>

                <line x1="170" y1="380" x2="163" y2="380" stroke="var(--info-ok)" stroke-width="1.5" marker-end="url(#fiArrowOk)" />
                <text x="140" y="371" text-anchor="middle" font-size="7.8" fill="var(--info-ok)">yes</text>
                <rect x="10" y="355" width="150" height="50" rx="9" fill="var(--info-ok-fill)" stroke="var(--info-ok)" />
                <text x="85" y="376" text-anchor="middle" font-size="8.6" font-weight="700" fill="var(--info-ok)">Completed / Skipped</text>
                <text x="85" y="391" text-anchor="middle" font-size="7.0" fill="var(--info-ok)">just finished vs already current</text>

                <path d="M85,405 L85,775 L300,775" fill="none" stroke="var(--info-ok)" stroke-width="1.4" />
                <line x1="300" y1="775" x2="300" y2="785" stroke="var(--info-ok)" stroke-width="1.4" marker-end="url(#fiArrowOk)" />

                <line x1="300" y1="430" x2="300" y2="453" stroke="currentColor" stroke-width="1.5" marker-end="url(#fiArrow2)" />
                <text x="316" y="446" font-size="7.8" fill="var(--theme-text-muted)">no</text>

                <polygon points="300,455 410,500 300,545 190,500" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="300" y="496" text-anchor="middle" font-size="7.8" fill="var(--theme-text)">Already mid-cycle?</text>
                <text x="300" y="509" text-anchor="middle" font-size="7.0" fill="var(--theme-text-muted)">(has the reset already happened?)</text>

                <line x1="300" y1="545" x2="300" y2="562" stroke="currentColor" stroke-width="1.5" marker-end="url(#fiArrow2)" />
                <text x="316" y="558" font-size="7.8" fill="var(--theme-text-muted)">no, fresh</text>

                <rect x="170" y="565" width="260" height="50" rx="8" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="300" y="586" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Clear old firmware</text>
                <text x="300" y="601" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">clears old firmware, opens a new attempt at 0%</text>

                <line x1="300" y1="615" x2="300" y2="632" stroke="currentColor" stroke-width="1.5" marker-end="url(#fiArrow2)" />

                <line x1="410" y1="500" x2="460" y2="500" stroke="currentColor" stroke-width="1.4" />
                <path d="M460,500 L460,650 L436,650" fill="none" stroke="currentColor" stroke-width="1.4" marker-end="url(#fiArrow2)" />
                <text x="466" y="494" font-size="7.8" fill="var(--theme-text-muted)">yes</text>
                <text x="466" y="624" font-size="7.4" fill="var(--theme-text-muted)">resume</text>

                <rect x="170" y="635" width="260" height="56" rx="9" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="300" y="657" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Device downloads in chunks</text>
                <text x="300" y="672" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">device requests byte ranges;</text>
                <text x="300" y="685" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">progress % tracked from the offset</text>

                <path d="M430,676 L500,676 L500,380 L436,380" fill="none" stroke="var(--theme-text-muted)" stroke-width="1.3" stroke-dasharray="3 3" marker-end="url(#fiArrowMuted2)" />
                <text x="506" y="524" font-size="7.4" fill="var(--theme-text-muted)">next login</text>

                <line x1="430" y1="585" x2="518" y2="585" stroke="var(--info-danger)" stroke-width="1.5" marker-end="url(#fiArrowDanger)" />
                <text x="474" y="576" text-anchor="middle" font-size="7.0" fill="var(--info-danger)">3 fails</text>

                <rect x="170" y="787" width="260" height="82" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="300" y="807" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Provision IP &amp; state, one at a time</text>
                <text x="300" y="825" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">State Enable &rarr; Primary IP &rarr; Secondary IP</text>
                <text x="300" y="840" text-anchor="middle" font-size="7.4" fill="var(--theme-text-muted)">&rarr; 2nd State Enable &rarr; 3rd IP &rarr; 4th IP</text>
                <text x="300" y="857" text-anchor="middle" font-size="7.0" fill="var(--theme-text-muted)">each step waits for the last to confirm</text>

                <line x1="300" y1="869" x2="300" y2="892" stroke="currentColor" stroke-width="1.5" marker-end="url(#fiArrow2)" />

                <rect x="150" y="895" width="300" height="52" rx="10" fill="var(--info-ok-fill)" stroke="var(--info-ok)" stroke-width="1.6" />
                <text x="300" y="917" text-anchor="middle" font-size="9.4" font-weight="700" fill="var(--info-ok)">Fully complete</text>
                <text x="300" y="933" text-anchor="middle" font-size="7.8" fill="var(--info-ok)">fully done, IP/state setup included</text>

                <text x="620" y="553" text-anchor="middle" font-size="8.2" font-weight="700" fill="var(--info-danger)" letter-spacing="0.03em">ABORTS FROM ANY STAGE</text>

                <rect x="520" y="565" width="200" height="40" rx="8" fill="var(--info-danger-fill)" stroke="var(--info-danger)" />
                <text x="620" y="589" text-anchor="middle" font-size="7.8" fill="var(--info-danger)">3 failed attempts in a row</text>

                <line x1="620" y1="605" x2="620" y2="614" stroke="var(--info-danger)" stroke-width="1.3" />
                <rect x="520" y="615" width="200" height="40" rx="8" fill="var(--info-danger-fill)" stroke="var(--info-danger)" />
                <text x="620" y="639" text-anchor="middle" font-size="7.8" fill="var(--info-danger)">No IP/state config for state</text>

                <line x1="620" y1="655" x2="620" y2="664" stroke="var(--info-danger)" stroke-width="1.3" />
                <rect x="520" y="665" width="200" height="40" rx="8" fill="var(--info-danger-fill)" stroke="var(--info-danger)" />
                <text x="620" y="689" text-anchor="middle" font-size="7.8" fill="var(--info-danger)">Target firmware missing/empty</text>

                <line x1="620" y1="705" x2="620" y2="714" stroke="var(--info-danger)" stroke-width="1.3" />
                <rect x="520" y="715" width="200" height="40" rx="8" fill="var(--info-danger-fill)" stroke="var(--info-danger)" />
                <text x="620" y="739" text-anchor="middle" font-size="7.8" fill="var(--info-danger)">User aborts (device or batch)</text>

                <line x1="620" y1="755" x2="620" y2="764" stroke="var(--info-danger)" stroke-width="1.3" />
                <rect x="520" y="765" width="200" height="40" rx="8" fill="var(--info-danger-fill)" stroke="var(--info-danger)" />
                <text x="620" y="789" text-anchor="middle" font-size="7.8" fill="var(--info-danger)">Superseded by higher priority</text>

                <line x1="620" y1="805" x2="620" y2="832" stroke="var(--info-danger)" stroke-width="1.4" marker-end="url(#fiArrowDanger)" />
                <rect x="520" y="835" width="200" height="52" rx="10" fill="var(--info-danger-fill)" stroke="var(--info-danger)" stroke-width="1.6" />
                <text x="620" y="857" text-anchor="middle" font-size="9.4" font-weight="700" fill="var(--info-danger)">Aborted</text>
                <text x="620" y="873" text-anchor="middle" font-size="7.4" fill="var(--info-danger)">flagged as aborted</text>
                <text x="620" y="885" text-anchor="middle" font-size="7.4" fill="var(--info-danger)">never matched again</text>

                <rect x="150" y="965" width="300" height="62" rx="10" fill="var(--info-pause-fill)" stroke="var(--info-pause)" stroke-dasharray="5 3" />
                <text x="300" y="986" text-anchor="middle" font-size="8.6" font-weight="700" fill="var(--info-pause)">Paused (non-AIS140 devices only)</text>
                <text x="300" y="1002" text-anchor="middle" font-size="7.4" fill="var(--info-pause)">idle 4h+ while Completed/Initiated/Skipped</text>
                <text x="300" y="1015" text-anchor="middle" font-size="7.4" fill="var(--info-pause)">&rarr; housekeeping flag, resumes normally next login</text>
              </svg>
              <figcaption class="fi-caption">
                The spine runs top to bottom on every login packet. A
                mismatch only triggers the clear-and-restart step the first
                time &mdash; on every later login within the same attempt, the
                device is already mid-download, so the server just lets it
                keep going (&ldquo;resume&rdquo;) instead of re-clearing. If a
                download finishes but the firmware still doesn't match on the
                very next login, that counts as a failed cycle and starts a
                fresh one; after three failed cycles in a row, the next
                attempt is blocked and the device is auto-aborted instead.
                <strong>Green</strong> is forward progress, <strong>red</strong>
                is any of the five ways a device gets permanently aborted (an
                aborted record is never matched again &mdash; a brand new
                batch or Manual FOTA request is required to try again), and
                <strong>purple</strong> is <code>Paused</code>, which is
                cosmetic bookkeeping only and always resolves back to
                <code>Completed</code> on the next successful login.
                <strong>Is FOTA Mandatory</strong> changes the &ldquo;Reported
                firmware = target version?&rdquo; check itself: while
                progress is under 100%, a mandatory device is forced down the
                &ldquo;no&rdquo; branch regardless of what firmware version it
                actually reports &mdash; it always goes through a real
                download instead of being allowed to short-circuit straight
                to <code>Skipped</code>.
              </figcaption>
            </figure>
          </section>

          <section class="fi-section">
            <h5 class="fi-h5">3. Terms you'll see in the device list</h5>
            <div class="fi-glossary">
              <div class="fi-term">
                <dt>Ping Count</dt>
                <dd>
                  Not a download-resume pointer &mdash; the device itself
                  tracks where its download left off. This counts how many
                  times the device reconnected while its FOTA cycle was still
                  unresolved. A high number means a flaky connection or a slow
                  download, not necessarily a problem.
                </dd>
              </div>
              <div class="fi-term">
                <dt>Download Attempts</dt>
                <dd>
                  A new attempt opens only when the firmware-clear handshake
                  restarts (a genuine reset), not on every dropped connection
                  mid-download. Each one closes as
                  <span class="fi-pill fi-pill-ok">Completed</span>,
                  <span class="fi-pill fi-pill-danger">Aborted</span>, or
                  Superseded/Skipped.
                </dd>
              </div>
              <div class="fi-term">
                <dt>Is FOTA Mandatory</dt>
                <dd>
                  Forces a real download even if the device already reports
                  the target firmware version. While progress is under 100%,
                  the version check is forced to report &ldquo;no
                  match&rdquo;, so a mandatory device can't skip straight to
                  <span class="fi-pill fi-pill-ok">Skipped</span> &mdash; it
                  has to actually flash and reach 100% before it's evaluated
                  normally.
                </dd>
              </div>
              <div class="fi-term">
                <dt>FOTA Status vs. Fully Complete</dt>
                <dd>
                  The FOTA status flips to Completed the moment the new
                  firmware is confirmed. Whether the device is fully complete
                  only becomes true once the IP/state provisioning steps
                  afterward all finish &mdash; a device can show Completed
                  FOTA while it's still not fully complete.
                </dd>
              </div>
              <div class="fi-term">
                <dt>Regular vs. AIS140 batch</dt>
                <dd>
                  Identical validation and download logic &mdash; the AIS140
                  flag changes nothing about how the firmware gets pushed. It
                  only changes what happens if the device is already claimed
                  by something else (Section 1) and, for auto-generated
                  batches, that the firmware version is picked automatically
                  rather than chosen by whoever uploaded the batch.
                </dd>
              </div>
            </div>
          </section>

          <div class="fi-callout">
            <strong>Not the same thing:</strong> a similarly-named
            &ldquo;OTA batch&rdquo; system pushes arbitrary one-off device
            commands (unrelated to firmware) through its own separate set of
            records. If a device has one of those queued, the server handles
            it first and FOTA is skipped for that login cycle &mdash; but
            it's a separate feature with its own Manual OTA / batch pages,
            not part of the Regular/AIS140 priority above.
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
